import { useState } from "react";
import { ArrowRight, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export function formatApiErrorDetail(detail, fallback = "Something went wrong. Please try again.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg.replace(/^Value error, /, "") : "")).filter(Boolean).join(" ") || fallback;
  return fallback;
}

export function PasswordSignIn({ email, setEmail, onSignedIn }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      await api.post("/auth/password/login", { email, password });
      toast.success("Signed in successfully");
      await onSignedIn();
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 429 ? formatApiErrorDetail(err.response?.data?.detail) : "Invalid e-mail address or password.");
    } finally { setSubmitting(false); }
  };

  const requestLink = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      setSent(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (forgot) {
    return (
      <form onSubmit={requestLink} className="space-y-6" noValidate data-testid="forgot-password-form">
        <div>
          <h1 className="font-display text-[36px] font-normal leading-tight text-jigsaw-navy">Set or reset your password</h1>
          <p className="mt-2 text-base leading-snug text-jigsaw-muted">We will e-mail a single-use link (valid for 60 minutes) to the address of a provisioned, active account. Nothing is revealed about other addresses.</p>
        </div>
        <div className="space-y-2">
          <label htmlFor="forgot-email" className="text-sm font-medium text-jigsaw-navy">E-mail address</label>
          <Input id="forgot-email" data-testid="forgot-email-input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@organisation.co.uk" className="h-12 rounded-lg border-jigsaw-border text-[15px]" />
        </div>
        {sent && (
          <div data-testid="forgot-sent-message" role="status" className="rounded-lg border border-jigsaw-border bg-[#F6F8FB] px-4 py-3 text-sm text-jigsaw-navy">
            {sent.message}
            {sent.dev_link && <p className="mt-2 break-all text-xs text-jigsaw-warning" data-testid="forgot-dev-link">Development only - no e-mail sent. Link: <a className="underline" href={`/set-password?token=${sent.dev_token}`}>{sent.dev_link}</a></p>}
          </div>
        )}
        {error && <p data-testid="forgot-error" role="alert" className="text-sm text-jigsaw-danger">{error}</p>}
        <Button data-testid="forgot-submit-button" type="submit" disabled={submitting || !email} className="h-12 w-full rounded-lg bg-jigsaw-orange text-[15px] font-semibold text-white hover:bg-jigsaw-orangeDark disabled:bg-jigsaw-orange/40 disabled:text-white">
          {submitting ? "Sending..." : "E-mail me a link"} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <button data-testid="forgot-back-button" type="button" onClick={() => { setForgot(false); setSent(null); setError(null); }} className="text-sm text-jigsaw-muted underline-offset-4 hover:underline">Back to sign in</button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" noValidate data-testid="password-login-form">
      <div>
        <h1 className="font-display text-[36px] font-normal leading-tight text-jigsaw-navy">Sign in with your password</h1>
        <p className="mt-2 text-base leading-snug text-jigsaw-muted">For accounts that have set a password. Access remains by invitation from Jigsaw Conferences.</p>
      </div>
      <div className="space-y-2">
        <label htmlFor="pw-email" className="text-sm font-medium text-jigsaw-navy">E-mail address</label>
        <Input id="pw-email" data-testid="password-email-input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@organisation.co.uk" className="h-12 rounded-lg border-jigsaw-border text-[15px]" />
      </div>
      <div className="space-y-2">
        <label htmlFor="pw-password" className="text-sm font-medium text-jigsaw-navy">Password</label>
        <Input id="pw-password" data-testid="password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="h-12 rounded-lg border-jigsaw-border text-[15px]" />
      </div>
      {error && <p data-testid="password-login-error" role="alert" className="text-sm text-jigsaw-danger">{error}</p>}
      <Button data-testid="password-login-button" type="submit" disabled={submitting || !email || !password} className="h-12 w-full rounded-lg bg-jigsaw-orange text-[15px] font-semibold text-white hover:bg-jigsaw-orangeDark disabled:bg-jigsaw-orange/40 disabled:text-white">
        {submitting ? "Signing in..." : "Sign in"} <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
      <button data-testid="forgot-password-link" type="button" onClick={() => { setForgot(true); setError(null); }} className="flex items-center gap-2 text-sm text-jigsaw-navy underline-offset-4 hover:underline">
        <KeyRound className="h-4 w-4 text-jigsaw-orange" /> Forgotten your password, or setting one for the first time?
      </button>
    </form>
  );
}
